import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { clearUserSessionLocal, setUserSession, markAuthBounce, getStoredUserId, isUserAuthenticated } from '../utils/auth';

function UserDashboard() {
  const navigate = useNavigate();
  const storedId = getStoredUserId();
  const hasLocalSession = isUserAuthenticated() && !!storedId;

  const [userId, setUserId] = useState(hasLocalSession ? storedId : null);

  const [profile, setProfile] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [family, setFamily] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [posts, setPosts] = useState([]); // ⭐ HEALTH POSTS
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Skip full-screen wait when we already have a session from login
  const [authLoading, setAuthLoading] = useState(!hasLocalSession);
  const [dataLoading, setDataLoading] = useState(hasLocalSession);
  const [prescriptionCount, setPrescriptionCount] = useState(0);

  /* ---------------- SESSION CHECK ---------------- */
  useEffect(() => {
    if (hasLocalSession) {
      // Show dashboard immediately; verify session in background
      loadAllData(storedId);
      api.get('user/session-check/')
        .then((res) => {
          if (res.data.authenticated) {
            setUserSession(res.data.user_id);
            setUserId(res.data.user_id);
          } else {
            markAuthBounce();
            clearUserSessionLocal();
            navigate('/user/login', { replace: true });
          }
        })
        .catch(() => {
          markAuthBounce();
          clearUserSessionLocal();
          navigate('/user/login', { replace: true });
        });
      return;
    }
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const res = await api.get('user/session-check/');
      if (res.data.authenticated) {
        const uid = res.data.user_id;
        setUserSession(uid);
        setUserId(uid);
        setAuthLoading(false);
        loadAllData(uid);
        return;
      }
    } catch (err) {
      console.error('Session check failed:', err);
    }

    setAuthLoading(false);
    markAuthBounce();
    clearUserSessionLocal();
    navigate('/user/login', { replace: true });
  };

  /* ---------------- LOAD ALL DATA ---------------- */

  const loadAllData = async (uid, showLoading = true) => {
    try {
      if (showLoading) setDataLoading(true);

      await Promise.all([
        fetchProfile(uid),
        fetchDoctors(),
        fetchFavorites(),
        fetchFamily(),
        fetchBookings(),
        fetchPrescriptionCount(),
        fetchHealthPosts(uid),
      ]);
      // Fetch notifications separately (don't block page load)
      fetchNotifications();
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      if (showLoading) setDataLoading(false);
    }
  };

  const fetchPrescriptionCount = async () => {
    try {
      const res = await api.get('prescriptions/user/list/');
      setPrescriptionCount(res.data.count ?? (res.data.prescriptions?.length || 0));
    } catch {
      setPrescriptionCount(0);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('notification/user/?limit=5');
      console.log("Notifications response:", res.data);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      // Set empty notifications on error to show "No notifications" message
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`notification/user/mark-read/${notificationId}/`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('notification/user/mark-all-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'APPOINTMENT_ACCEPTED':
        return 'bi-check-circle-fill text-success';
      case 'APPOINTMENT_REJECTED':
        return 'bi-x-circle-fill text-danger';
      case 'APPOINTMENT_RESCHEDULED':
        return 'bi-calendar-event text-warning';
      case 'APPOINTMENT_CANCELLED':
        return 'bi-calendar-x text-secondary';
      case 'PAYMENT_SUCCESS':
        return 'bi-credit-card-fill text-success';
      case 'APPOINTMENT_REMINDER':
        return 'bi-bell-fill text-info';
      default:
        return 'bi-bell-fill text-primary';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchProfile = async (uid) => {
    const res = await api.get(`user/profile/${uid}/`);
    setProfile(res.data);
  };

  const fetchDoctors = async () => {
    const res = await api.get('user/doctor_list/');
    setDoctors(res.data);
  };

  const fetchFavorites = async () => {
    const res = await api.get('user/favorites/');
    setFavorites(res.data);
  };

  const fetchFamily = async () => {
    const res = await api.get('user/family/');
    setFamily(res.data);
  };

  const fetchBookings = async () => {
    const res = await api.get('appointments/user/my-bookings/');
    setBookings(res.data);
  };

  /* ---------------- HEALTH POSTS ---------------- */

  const fetchHealthPosts = async (uid = null) => {
    const currentUserId = uid || userId;
    if (!currentUserId) return;
    try {
      const res = await api.get('social/posts/', {
        params: { user_id: currentUserId }
      });
      // Sort by newest first and take first 2 for dashboard
      const sortedPosts = res.data.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      ).slice(0, 2);
      setPosts(sortedPosts);
    } catch (err) {
      console.error("Error fetching posts:", err);
    }
  };

  /* ---------------- FAVORITES ---------------- */

  const isFavorite = (doctorId) =>
    favorites.some((f) => f?.doctor_id === doctorId);

  const toggleFavorite = async (doctorId) => {
    const wasFavorite = isFavorite(doctorId);

    // Optimistic UI update - instant feedback
    if (wasFavorite) {
      setFavorites(prev => prev.filter(f => f?.doctor_id !== doctorId));
    } else {
      setFavorites(prev => [...prev, { doctor_id: doctorId }]);
    }

    try {
      if (wasFavorite) {
        const response = await api.post('user/favorites/remove/', { doctor_id: doctorId });
        // Use response data if available, otherwise refresh
        if (response.data?.favorites) {
          setFavorites(response.data.favorites);
        } else {
          const res = await api.get('user/favorites/');
          setFavorites(res.data);
        }
      } else {
        const response = await api.post('user/favorites/add/', { doctor_id: doctorId });
        // Use response data if available, otherwise refresh
        if (response.data?.favorites) {
          setFavorites(response.data.favorites);
        } else {
          const res = await api.get('user/favorites/');
          setFavorites(res.data);
        }
      }
    } catch (err) {
      // Revert on error - try to refresh from backend
      console.error("Failed to update favorites:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error data:", err.response?.data);
      console.error("Error message:", err.message);

      // Show specific error message
      const errorMessage = err.response?.data?.error ||
        err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Failed to update favorites. Please try again.";

      // If it's a 401, redirect to login
      if (err.response?.status === 401) {
        navigate('/user/login');
        return;
      }

      try {
        // Try to refresh favorites from backend
        const res = await api.get('user/favorites/');
        setFavorites(res.data);
      } catch (refreshErr) {
        // If refresh also fails, just log it
        console.error("Failed to refresh favorites:", refreshErr);
        // If it's a 401, redirect to login
        if (refreshErr.response?.status === 401) {
          navigate('/user/login');
          return;
        }
      }

      // Only show alert if it's not a 404 (doctor not found in favorites is okay)
      if (err.response?.status !== 404) {
        toast.error(`Error: ${errorMessage}\nStatus: ${err.response?.status || 'Unknown'}`, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    }
  };

  // Helper function to get image URL
  const getDoctorImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/300x300?text=Doctor";

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    // Add /media/ prefix for paths like "doctor_images/filename.png"
    return `${apiUrl}/media/${image}`;
  };

  // Helper function to get user image URL
  const getUserImageUrl = (image) => {
    if (!image) return null;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };


  /* ---------------- FAMILY ---------------- */
  // Family member management is now in UserFamily page

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-soft min-vh-100">
      <UserNavbar />

      <div className="container py-4">

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col-12 col-md-8">
            <h2 className="fw-bold text-dark mb-0 fs-4 fs-md-3">
              Welcome back, {profile?.name || (dataLoading ? '...' : 'User')}!
            </h2>
            <p className="text-muted mb-0 mb-md-2">Here's what's happening with your health today.</p>
          </div>
        </div>

        {dataLoading && (
          <div className="text-center mb-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        )}

        <div className="row g-4">

          {/* LEFT COLUMN: STATS & FAMILY */}
          <div className="col-lg-4">

            {/* PROFILE CARD */}
            <div className="card border-0 shadow-hover rounded-4 mb-4">
              <div className="card-body p-4 text-center">
                <div className="d-inline-block mb-3">
                  {profile?.image ? (
                    <img
                      src={getUserImageUrl(profile?.image)}
                      alt={profile?.name || 'User'}
                      className="rounded-circle object-fit-cover border border-2 border-primary"
                      style={{ width: '80px', height: '80px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <div
                      className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: '80px',
                        height: '80px'
                      }}
                    >
                      <i className="bi bi-person-fill fs-1"></i>
                    </div>
                  )}
                  {/* <div 
                    className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      cursor: 'pointer',
                      display: profile?.image ? 'none' : 'flex'
                    }}
                    onClick={() => navigate('/user/profile')}
                  >
                    <i className="bi bi-person-fill fs-1"></i>
                  </div> */}
                </div>
                {/* <div 
                  className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                  style={{ 
                    width: '80px', 
                    height: '80px',
                    display: profile?.image ? 'none' : 'flex'
                  }}
                >
                  <i className="bi bi-person-fill fs-1"></i>
                </div> */}
                <h5
                  className="fw-bold mb-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate('/user/profile')}
                  title="Click to view profile"
                >
                  {profile?.name}
                </h5>
                <p className="text-muted small mb-3">{profile?.email}</p>
                <div className="d-flex justify-content-around border-top pt-3">
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/user/bookings')}
                    title="View upcoming bookings"
                  >
                    <h6 className="fw-bold mb-0">{bookings.filter(b => {
                      if (b.booking_type !== 'UPCOMING') return false;
                      // Never count finished / cancelled bookings as upcoming
                      return !['COMPLETED', 'MISSED', 'CANCELLED', 'REJECTED'].includes(b.status);
                    }).length}</h6>
                    <small className="text-muted">Upcoming</small>
                  </div>
                  <div className="border-start"></div>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/user/favorites')}
                    title="View favorites"
                  >
                    <h6 className="fw-bold mb-0">{favorites.length}</h6>
                    <small className="text-muted">Favorites</small>
                  </div>
                </div>
              </div>
            </div>

            {/* QUICK ACTIONS */}
            <div className="card border-0 shadow-sm rounded-4 bg-primary text-white mb-4">
              <div className="card-body mt-3 p-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-white bg-opacity-20 rounded-circle p-2 me-2">
                    <i className="bi bi-hand-raised-fill"></i>
                  </div>
                  <h5 className="fw-bold mb-0">Need Help?</h5>
                </div>
                <p className="small mb-3 text-white-50">Find top-rated specialists and book your appointment in minutes.</p>

                {/* Features List */}
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2 text-white">
                    <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '0.9rem' }}></i>
                    <span className="small">24/7 Doctor Availability</span>
                  </div>
                  <div className="d-flex align-items-center mb-2 text-white">
                    <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '0.9rem' }}></i>
                    <span className="small">Instant Appointment Booking</span>
                  </div>
                  <div className="d-flex align-items-center text-white">
                    <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '0.9rem' }}></i>
                    <span className="small">Expert Medical Advice</span>
                  </div>
                </div>

                <button className="btn btn-light w-100 fw-bold mt-2" onClick={() => navigate('/user/doctor_list')}>
                  <i className="bi bi-search me-2"></i>Find a Doctor
                </button>
              </div>
            </div>

            {/* NOTIFICATIONS */}
            <div className="card border-0 shadow-hover rounded-4 mb-4">
              <div className="card-body" style={{ height: '220px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-bell me-2 text-primary"></i>Notifications
                    {unreadCount > 0 && (
                      <span className="badge bg-danger rounded-pill ms-2">{unreadCount}</span>
                    )}
                  </h5>
                  <div className="d-flex gap-2">
                    {unreadCount > 0 && (
                      <button
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={markAllNotificationsRead}
                        title="Mark all as read"
                      >
                        <i className="bi bi-check-all"></i>
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-primary rounded-pill"
                      onClick={() => navigate('/user/notifications')}
                      title="View all notifications"
                    >
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>

                <div className="list-group list-group-flush" style={{ height: '150px', overflow: 'hidden' }}>
                  {notifications.length === 0 ? (
                    <div className="text-center py-3">
                      <i className="bi bi-bell-slash text-muted fs-1 d-block mb-2"></i>
                      <p className="text-muted small mb-0">No notifications yet</p>
                    </div>
                  ) : (
                    <>
                      {notifications.slice(0, 1).map((notification) => (
                        <div
                          key={notification.id}
                          className={`list-group-item px-0 border-0 d-flex align-items-start py-2 ${!notification.is_read ? 'bg-light bg-opacity-50' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (!notification.is_read) {
                              markNotificationRead(notification.id);
                            }
                          }}
                        >
                          <div className={`bg-light rounded-circle p-2 me-3`}>
                            <i className={`bi ${getNotificationIcon(notification.notification_type)}`}></i>
                          </div>
                          <div className="flex-grow-1">
                            <p className={`mb-0 small ${!notification.is_read ? 'fw-bold' : ''}`}>
                              {notification.title}
                            </p>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {notification.message.length > 50
                                ? notification.message.substring(0, 50) + '...'
                                : notification.message}
                            </small>
                            <div className="mt-1">
                              <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {formatTimeAgo(notification.created_at)}
                              </small>
                            </div>
                          </div>
                          {!notification.is_read && (
                            <span className="badge bg-primary rounded-circle p-1" style={{ width: '8px', height: '8px' }}></span>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* HEALTH RECORDS */}
            {/* <div className="card border-0 shadow-hover rounded-4 mb-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-file-medical me-2 text-success" />
                      Health Records
                    </h5>
                    <p className="text-muted small mb-0">
                      {prescriptionCount > 0
                        ? `${prescriptionCount} prescription${prescriptionCount > 1 ? 's' : ''} on file`
                        : 'Prescriptions & documents after completed visits'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-success rounded-pill"
                    onClick={() => navigate('/user/health-records')}
                  >
                    Open
                  </button>
                </div>
              </div>
            </div> */}

            {/* FAMILY MEMBERS */}
            <div className="card border-0 shadow-hover rounded-4 mb-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-people me-2 text-primary"></i>Family
                    {family.length > 0 && (
                      <span className="badge bg-primary rounded-pill ms-2">{family.length}</span>
                    )}
                  </h5>
                  <button
                    className="btn btn-sm btn-primary rounded-pill"
                    onClick={() => navigate('/user/family')}
                    title="View all family members"
                  >
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </div>

                <div className="list-group list-group-flush">
                  {dataLoading ? (
                    <div className="text-center py-4 d-flex flex-column justify-content-center align-items-center">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : family.length === 0 ? (
                    <div className="text-center py-4 d-flex flex-column justify-content-center">
                      <i className="bi bi-people text-muted fs-1 d-block mb-2"></i>
                      <p className="text-muted small mb-3">No family members yet.</p>
                      <button
                        className="btn btn-sm btn-primary rounded-pill"
                        onClick={() => navigate('/user/family')}
                      >
                        <i className="bi bi-person-plus me-1"></i>Add Member
                      </button>
                    </div>
                  ) : (
                    <>
                      {family.slice(0, 2).map((m) => (
                        <div
                          key={m.id}
                          className="list-group-item px-0 border-0 d-flex align-items-center justify-content-between py-3"
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-3" style={{ width: '40px', height: '40px' }}>
                              <i className="bi bi-person-fill d-flex align-items-center justify-content-center text-white h-100"></i>
                            </div>
                            <div className="flex-grow-1">
                              <p className="mb-0 fw-medium">{m.name}</p>
                              <small className="text-muted">
                                <span className="badge bg-light text-dark rounded-pill" style={{ fontSize: '0.7rem' }}>
                                  {m.relation}
                                </span>
                              </small>
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary rounded-pill"
                            onClick={() => navigate('/user/family')}
                            title="Book appointment"
                            style={{ flexShrink: 0 }}
                          >
                            <i className="bi bi-calendar-plus"></i>
                          </button>
                        </div>
                      ))}
                      {family.length > 2 && (
                        <div className="text-center pt-2 pb-2">
                          <button
                            className="btn btn-link text-primary text-decoration-none p-0 small"
                            onClick={() => navigate('/user/family')}
                          >
                            View all {family.length} members <i className="bi bi-arrow-right ms-1"></i>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>



          </div>

          {/* MIDDLE COLUMN: HEALTH TIPS & BOOKINGS */}
          <div className="col-lg-8">

            {/* UPCOMING BOOKINGS */}
            <div className="card border-0 shadow-hover rounded-4 mb-4" style={{ height: '543px' }}>
              <div className="card-body d-flex flex-column h-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-calendar-check me-2 text-primary"></i>My Appointments
                  </h5>

                  <button
                    className="btn btn-sm btn-primary rounded-pill"
                    onClick={() => navigate('/user/bookings')}
                  >
                    <i className="bi bi-arrow-right me-1"></i>View All
                  </button>
                </div>

                {dataLoading ? (
                  <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  </div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                    <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <i className="bi bi-calendar-x text-white fs-1"></i>
                    </div>
                    <h6 className="fw-bold text-dark mb-2">No Appointments Yet</h6>
                    <p className="text-muted small mb-4">Start by booking your first appointment with our expert doctors</p>

                  </div>
                ) : (
                  <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
                    <table className="table table-hover align-middle">
                      <thead className="table-light d-none d-md-table-header-group">
                        <tr>
                          <th>Doctor</th>
                          <th>Date & Time</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings
                          .sort((a, b) => {
                            // Sort by appointment_id to show latest booked first
                            return b.appointment_id - a.appointment_id;
                          })
                          .slice(0, 8)
                          .map((b) => (
                            <tr key={b.appointment_id}>
                              <td data-label="Doctor">
                                <div className="fw-bold">{b.doctor_name}</div>
                                <small className="text-muted">Specialist</small>
                              </td>
                              <td data-label="Date & Time">
                                <div className="small">{b.date}</div>
                                <div className="text-primary fw-bold small">{b.start_time} - {b.end_time}</div>
                              </td>
                              <td data-label="Status">
                                <span className={`badge rounded-pill ${b.status === 'CONFIRMED' || b.status === 'ACCEPTED' ? 'bg-success' :
                                  b.status === 'PENDING' ? 'bg-warning text-dark' :
                                    b.status === 'REJECTED' ? 'bg-danger' : 'bg-secondary'
                                  }`}>
                                  {b.status}
                                </span>
                              </td>
                              <td data-label="Action">
                                <button
                                  className="btn btn-sm btn-outline-primary rounded-pill w-100 w-md-auto"
                                  onClick={() => {
                                    setSelectedBooking(b);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* HEALTH TIPS FEED */}
            <div className="d-flex justify-content-between align-items-center mb-3 mt-5">
              <h5 className="fw-bold mb-0"><i className="bi bi-lightbulb me-2 text-warning"></i>Health Tips from Doctors</h5>
              <button className="btn btn-link text-decoration-none p-0 fw-medium" onClick={() => navigate('/user/feed')}>
                View More <i className="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
            <div className="row g-3">
              {posts.length === 0 && (
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5 text-muted">No health tips available</div>
                  </div>
                </div>
              )}

              {posts.map((post) => {
                const shortContent = post.short_content || post.large_content || '';
                const textContent = shortContent.replace(/<[^>]*>/g, '');
                const displayContent = textContent.length > 120
                  ? textContent.substring(0, 120) + '...'
                  : textContent;

                return (
                  <div key={post.id} className="col-12 col-md-6">
                    <div className="card border-0 shadow-hover h-100 rounded-4" style={{ cursor: 'pointer' }} onClick={() => navigate(`/post/${post.id}`)}>
                      {post.image && (
                        <div style={{ height: '220px', overflow: 'hidden' }}>
                          <img
                            src={post.image.startsWith('http') ? post.image : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/media/${post.image}`}
                            alt={post.title || 'Post'}
                            className="w-100 h-100"
                            style={{ objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="card-body p-4 d-flex flex-column">
                        <div className="d-flex align-items-center mb-3">
                          <div className="bg-info bg-opacity-10 text-info rounded-circle p-2 me-2" style={{ width: '40px', height: '40px' }}>
                            <i className="bi bi-person-check-fill d-flex align-items-center justify-content-center h-100"></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">{post.doctor_name}</h6>
                            <small className="text-muted">{new Date(post.created_at).toLocaleDateString()}</small>
                          </div>
                        </div>
                        {post.title && (
                          <h6 className="fw-bold mb-3">{post.title}</h6>
                        )}
                        <div
                          className="card-text mb-3 flex-grow-1"
                          style={{ fontSize: '0.9rem', color: '#444', lineHeight: '1.6', minHeight: '80px' }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(displayContent)
                          }}
                        />
                        <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-auto">
                          <div className="d-flex gap-3">
                            <span className="small text-muted"><i className="bi bi-heart-fill text-danger me-1"></i>{post.likes_count || 0}</span>
                            <span className="small text-muted"><i className="bi bi-chat-dots-fill text-primary me-1"></i>{post.comments?.length || 0}</span>
                          </div>
                          <button
                            className="btn btn-primary btn-sm rounded-pill px-3"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/post/${post.id}`);
                            }}
                          >
                            More <i className="bi bi-arrow-right ms-1"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        {/* DOCTORS SECTION */}
        <div className="mt-5 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0"><i className="bi bi-star-fill me-2 text-warning"></i>Recommended Doctors</h5>
            <button className="btn btn-link text-decoration-none" onClick={() => navigate('/user/doctor_list')}>View All</button>
          </div>
          <div className="row g-3">
            {doctors.slice(0, 4).map((doc) => (
              <div key={doc.id} className="col-md-3">
                <div className="card border-0 shadow-sm text-center h-100 position-relative">
                  <div className="position-absolute top-0 end-0 m-2">
                    <button
                      className="btn btn-sm border-0"
                      onClick={() => toggleFavorite(doc.id)}
                      style={{
                        background: 'transparent',
                        padding: '0',
                        boxShadow: 'none'
                      }}
                      title={isFavorite(doc.id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`bi ${isFavorite(doc.id) ? 'bi-heart-fill text-danger' : 'bi-heart text-muted'} fs-5`}></i>
                    </button>
                  </div>
                  <div className="card-body p-4">
                    <div className="mb-3 position-relative">
                      <img
                        src={getDoctorImageUrl(doc.image)}
                        alt={doc.name}
                        className="rounded-circle object-fit-cover"
                        style={{ width: '80px', height: '80px' }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/300x300?text=Doctor";
                        }}
                      />
                    </div>
                    <h6 className="fw-bold mb-1">{doc.name}</h6>
                    <p className="text-muted small mb-1">{doc.specialization || 'General Physician'}</p>
                    <div className="d-flex align-items-center justify-content-center mb-3">
                      <div className="text-warning me-2" style={{ fontSize: '0.8rem' }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`bi ${i < Math.floor(doc.average_rating || 0) ? 'bi-star-fill' : (i < (doc.average_rating || 0) ? 'bi-star-half' : 'bi-star')} me-1`}
                          ></i>
                        ))}
                      </div>
                      <span className="fw-bold text-dark small me-1">{doc.average_rating || '0.0'}</span>
                      <span className="text-muted" style={{ fontSize: '0.7rem' }}>({doc.total_reviews || 0})</span>
                    </div>
                    <button
                      className="btn btn-primary btn-sm w-100 rounded-pill"
                      onClick={() => navigate(`/user/doctor-profile/${doc.id}`)}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Floating Chatbot / Message Icon */}
      <button
        type="button"
        className="btn btn-primary rounded-circle shadow-lg border-0 d-flex align-items-center justify-content-center p-0"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          zIndex: 1050,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(13, 110, 253, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '';
        }}
        onClick={() => navigate('/user/chat')}
        title="Chat with us"
        aria-label="Open chat"
      >
        <i className="bi bi-chat-dots-fill fs-4 text-white" aria-hidden="true"></i>
      </button>

      {/* APPOINTMENT DETAIL MODAL */}
      {showDetailModal && selectedBooking && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Appointment Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4 text-center">
                  <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-person-badge text-white fs-2"></i>
                  </div>
                  <h5 className="fw-bold mb-0">{selectedBooking.doctor_name}</h5>
                  <p className="text-muted small">Specialist</p>
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Date</label>
                    <p className="fw-medium mb-0">
                      <i className="bi bi-calendar-event me-2 text-primary"></i>
                      {selectedBooking.date || 'N/A'}
                    </p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Time</label>
                    <p className="fw-medium mb-0">
                      <i className="bi bi-clock me-2 text-primary"></i>
                      {selectedBooking.start_time && selectedBooking.end_time
                        ? `${selectedBooking.start_time} - ${selectedBooking.end_time}`
                        : selectedBooking.cancelled_slot_time || 'N/A'}
                    </p>
                  </div>
                  <div className="col-12 border-top pt-3">
                    <label className="text-muted small fw-bold text-uppercase d-block">Status</label>
                    <span className={`badge rounded-pill px-3 py-2 ${selectedBooking.status === 'ACCEPTED' || selectedBooking.status === 'CONFIRMED' ? 'bg-success' :
                      selectedBooking.status === 'PENDING' ? 'bg-warning text-dark' :
                        selectedBooking.status === 'REJECTED' ? 'bg-danger' :
                          selectedBooking.status === 'CANCELLED' ? 'bg-secondary' : 'bg-secondary'
                      }`}>
                      {selectedBooking.status}
                    </span>
                  </div>
                  {selectedBooking.rejection_reason && (
                    <div className="col-12 border-top pt-3">
                      <label className="text-muted small fw-bold text-uppercase d-block">
                        {selectedBooking.status === 'CANCELLED' ? 'Cancellation Reason' : 'Rejection Reason'}
                      </label>
                      <p className="mb-0 text-muted">{selectedBooking.rejection_reason}</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 d-grid">
                  <button className="btn btn-primary rounded-3 fw-bold" onClick={() => setShowDetailModal(false)}>
                    Close
                  </button>
                  <button
                    className="btn btn-outline-primary rounded-3 mt-2"
                    onClick={() => {
                      setShowDetailModal(false);
                      navigate('/user/bookings');
                    }}
                  >
                    View All Appointments
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDashboard;

